import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  GetLeadQualificationSettingsRequestInterface,
  LeadQualificationSettingsInterface,
  LeadsApiService,
  ReplaceLeadQualificationSettingsRequestInterface,
} from '@vendasta/prospect';
import { BehaviorSubject, EMPTY, Observable, combineLatest } from 'rxjs';
import { catchError, map, retry, switchMap, take } from 'rxjs/operators';

@Injectable()
export class LeadQualificationSettingsService {
  private readonly isLoading$$ = new BehaviorSubject<boolean>(false);
  readonly isLoading$ = this.isLoading$$.asObservable();

  private readonly settings$$ = new BehaviorSubject<LeadQualificationSettingsInterface>({});
  readonly settings$ = this.settings$$.asObservable();

  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private readonly leadsSDK: LeadsApiService,
    private readonly snackbarService: SnackbarService,
    private translateService: TranslateService,
  ) {}

  getLeadQualificationSettings(): void {
    this.partnerId$
      .pipe(
        map(
          (pid) =>
            <GetLeadQualificationSettingsRequestInterface>{
              partnerId: pid,
            },
        ),
        switchMap((request) => this.leadsSDK.getLeadQualificationSettings(request)),
        retry(1),
        catchError(() => {
          this.snackbarService.errorSnack(
            this.translateService.instant('LEAD_INTEGRATIONS.SETTINGS.LEAD_QUALIFICATION.LOAD_ERROR'),
          );
          return EMPTY;
        }),
        map(
          (response) =>
            <LeadQualificationSettingsInterface>{
              createAccountGroup: response.leadQualificationSettings.createAccountGroup || false,
              logActivityAndReassign: response.leadQualificationSettings.logActivityAndReassign || false,
            },
        ),
        take(1),
      )
      .subscribe((settings) => {
        this.settings$$.next(settings);
      });
  }

  setNoDupesTakeAction(cagtakeAction: boolean): void {
    const settings = this.settings$$.getValue();
    settings.createAccountGroup = cagtakeAction;
    this.settings$$.next(settings);
  }

  setDupesTakeAction(takeAction: boolean): void {
    const settings = this.settings$$.getValue();
    settings.logActivityAndReassign = takeAction;
    this.settings$$.next(settings);
  }

  save(): Observable<void> {
    return combineLatest([this.partnerId$, this.settings$]).pipe(
      map(
        ([partner, settings]) =>
          <ReplaceLeadQualificationSettingsRequestInterface>{
            partnerId: partner,
            leadQualificationSettings: settings,
          },
      ),
      switchMap((request) => this.leadsSDK.replaceLeadQualificationSettings(request)),
      retry(1),
      catchError(() => {
        this.snackbarService.errorSnack(this.translateService.instant('LEAD_INTEGRATIONS.SETTINGS.COMMON.SAVE_ERROR'));
        return EMPTY;
      }),
      map((_) => null),
      take(1),
    );
  }
}
